'use client'

/**
 * Live frames from `GET /api/autos/stream` (plans/01 3.5, 3.6).
 *
 * One connection per window serves the shell: it opens while anything listens (`onAutoFrame`, `onStoryStep`, or a
 * `useAutoStream()` without a run or record), hands every frame to `publishAutoFrame` so the story clock, the ticker and the
 * nav badges refetch, and closes when the last listener leaves. A hook asking for one run or one record (`runId`, `ref`)
 * opens its own filtered stream that replays from the start and does not publish, so no frame reaches the shell twice.
 *
 * A hidden tab closes its streams and reopens them with `since` when shown, so background tabs never hold the browser's few
 * connections to the API host.
 *
 * It reads with `fetch` and the bearer header (a token never goes in a URL, Rule 13 section 4), keeps the last 200 frames,
 * and reconnects after the server rotates the stream (`bye`), a network error or a 401 (the session is read again first, so a
 * refreshed token is used), with backoff 1 s, 2 s, 5 s. Missed events are replayed from `since`, so a drop never loses a step.
 * The server never sends a frame ahead of the story clock; pages refetch on `dr.story.step`.
 */

import { useMemo, useSyncExternalStore } from 'react'
import { getSession } from 'next-auth/react'
import { readSseStream, SseHttpError } from '@/lib/sse'

export type AutoFrameStatus = 'running' | 'ok' | 'held' | 'error' | 'skipped'

export type AutoFrame = {
  id: number
  run_id: string | null
  correlation_ref: string | null
  operator: string | null
  employee: { id: string; name: string; monogram: string } | null
  actor?: string
  kind: string
  status: AutoFrameStatus
  step_id: string | null
  text: string
  ref: string | null
  item_id: string | null
  rule_code: string | null
  at_min: number
  links: { record: string | null; policy: string | null }
  run: { status: string; source: string; intent: string; is_replay: boolean; replay_label: string | null } | null
  /** Present on `dr.graph.upserted` only (plans/13 section 4.5.7). */
  graph?: GraphUpsertFrame | null
}

/** What the live Auto Graph mirror added in one pass: at most 60 records plus links; `more` means the rest is in the feed. */
export type GraphUpsertFrame = {
  seq: number
  step_id: string
  step_label: string | null
  reason: string
  at?: string
  clock_min: number
  source: 'tenant' | 'local'
  nodes: { id: string; key: string; label: string; layer: string; title: string; extends?: string | null }[]
  edges: { src: string; dst: string; type: string }[]
  documents: string[]
  memories: number
  counts: { nodes: number; edges: number; documents: number; memories: number; forgotten?: number }
  more: boolean
}

export interface AutoStreamOptions {
  runId?: string
  ref?: string
  kinds?: string[]
}

export interface AutoStreamState {
  frames: AutoFrame[]
  connected: boolean
  lastId: number | null
}

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:8001'
const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || ''
const MAX_FRAMES = 200
const BACKOFF_MS = [1000, 2000, 5000]
const IDLE: AutoStreamState = { frames: [], connected: false, lastId: null }

function isAutoFrame(value: unknown): value is AutoFrame {
  return (
    !!value &&
    typeof value === 'object' &&
    typeof (value as AutoFrame).id === 'number' &&
    typeof (value as AutoFrame).kind === 'string'
  )
}

function streamUrl(opts: { runId?: string; ref?: string }, since: number | null): string {
  const params = new URLSearchParams()
  if (since !== null) params.set('since', String(since))
  if (opts.runId) params.set('run_id', opts.runId)
  if (opts.ref) params.set('ref', opts.ref)
  const query = params.toString()
  return `${API_URL}${BASE_PATH}/api/autos/stream${query ? `?${query}` : ''}`
}

/** A kind filter entry matches exactly, or as a prefix when it ends with a dot ("dr." matches every story frame). */
function matchesKinds(frame: AutoFrame, kinds?: string[]): boolean {
  if (!kinds || kinds.length === 0) return true
  return kinds.some((k) => (k.endsWith('.') ? frame.kind.startsWith(k) : frame.kind === k))
}

// ─── Stream reader (shared by the window stream and a filtered one) ─────────

interface ReaderHandle {
  stop: () => void
}

function tabHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden'
}

function startReader(
  opts: { runId?: string; ref?: string },
  onFrame: (frame: AutoFrame) => void,
  onConnected: (connected: boolean) => void,
  initialSince: number | null,
): ReaderHandle {
  let cancelled = false
  let attempt = 0
  let lastId = initialSince
  let current: AbortController | null = null
  let wake: (() => void) | null = null

  // A wait ends early when the tab becomes visible again (or the reader stops), so a returning tab reconnects at once.
  const wait = (ms: number) =>
    new Promise<void>((resolve) => {
      const done = () => {
        clearTimeout(timer)
        wake = null
        resolve()
      }
      const timer = setTimeout(done, ms)
      wake = done
    })

  const untilVisible = () =>
    new Promise<void>((resolve) => {
      if (!tabHidden() || cancelled) {
        resolve()
        return
      }
      wake = () => {
        wake = null
        resolve()
      }
    })

  // A hidden tab lets go of its connection: browsers allow about six per host across all tabs, and a held stream in a
  // background tab queues every other request (A2-07). It reconnects with `since` when shown, so no frame is lost.
  const onVisibility = () => {
    if (tabHidden()) current?.abort()
    wake?.()
  }
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility)

  const connect = async () => {
    while (!cancelled) {
      if (tabHidden()) {
        await untilVisible()
        continue
      }
      const controller = new AbortController()
      current = controller
      try {
        const session = await getSession()
        const headers: Record<string, string> = {}
        if (session?.accessToken) headers.Authorization = `Bearer ${session.accessToken}`
        await readSseStream(streamUrl(opts, lastId), {
          headers,
          signal: controller.signal,
          onHeartbeat: () => onConnected(true),
          onFrame: ({ event, data }) => {
            if (event !== 'auto' || !isAutoFrame(data)) return
            attempt = 0
            if (lastId !== null && data.id <= lastId) return
            lastId = data.id
            onConnected(true)
            onFrame(data)
          },
        })
        // The server closed the stream after its rotation window: reconnect straight away with `since`.
        attempt = 0
        await wait(250)
      } catch (error) {
        if (cancelled) break
        if (error instanceof Error && error.name === 'AbortError') continue // the tab was hidden: wait until it is shown
        onConnected(false)
        // A 401 re-reads the session on the next loop; every error backs off 1 s, 2 s, then 5 s.
        const delay = BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)]
        attempt += 1
        if (error instanceof SseHttpError && error.status === 401) await getSession()
        await wait(delay)
      } finally {
        if (current === controller) current = null
      }
    }
  }

  void connect()
  return {
    stop: () => {
      cancelled = true
      current?.abort()
      wake?.()
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility)
    },
  }
}

// ─── Listener registry and the shared window stream ────────────────────────

type FrameListener = (frame: AutoFrame) => void

const frameListeners = new Set<FrameListener>()
const storyStepListeners = new Set<() => void>()
const stateListeners = new Set<() => void>()

let shared: AutoStreamState = IDLE
let sharedReader: ReaderHandle | null = null
let windowSubscribers = 0

function setShared(next: AutoStreamState) {
  shared = next
  stateListeners.forEach((listener) => listener())
}

function acquireWindowStream() {
  windowSubscribers += 1
  if (windowSubscribers === 1 && typeof window !== 'undefined') {
    sharedReader = startReader(
      {},
      (frame) => {
        setShared({ frames: [...shared.frames, frame].slice(-MAX_FRAMES), connected: true, lastId: frame.id })
        publishAutoFrame(frame)
      },
      (connected) => {
        if (shared.connected !== connected) setShared({ ...shared, connected })
      },
      null,
    )
  }
}

function releaseWindowStream() {
  windowSubscribers -= 1
  if (windowSubscribers === 0 && sharedReader) {
    sharedReader.stop()
    sharedReader = null
    setShared(IDLE)
  }
}

/** Called for every frame the stream delivers. Returns an unsubscribe function. */
export function onAutoFrame(listener: FrameListener): () => void {
  frameListeners.add(listener)
  acquireWindowStream()
  return () => {
    frameListeners.delete(listener)
    releaseWindowStream()
  }
}

/** Called on each `dr.story.step` frame, so pages refetch their clock-filtered data. */
export function onStoryStep(listener: () => void): () => void {
  storyStepListeners.add(listener)
  acquireWindowStream()
  return () => {
    storyStepListeners.delete(listener)
    releaseWindowStream()
  }
}

/**
 * Calls `fn` once, `delayMs` after the last of a burst of matching frames. A stream (re)connect replays its backlog as a quick
 * run of frames; a page that refetches on frames should do it once for the run, not once per frame (A2-07).
 */
export function onAutoFrames(match: (frame: AutoFrame) => boolean, fn: () => void, delayMs = 400): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null
  const off = onAutoFrame((frame) => {
    if (!match(frame)) return
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => {
      timer = null
      fn()
    }, delayMs)
  })
  return () => {
    if (timer) clearTimeout(timer)
    off()
  }
}

/** Hands one frame to every listener. The stream reader calls this for each frame it parses. */
export function publishAutoFrame(frame: AutoFrame): void {
  frameListeners.forEach((listener) => listener(frame))
  if (frame.kind === 'dr.story.step') storyStepListeners.forEach((listener) => listener())
}

// ─── Hook ──────────────────────────────────────────────────────────────────

function subscribeShared(listener: () => void): () => void {
  stateListeners.add(listener)
  acquireWindowStream()
  return () => {
    stateListeners.delete(listener)
    releaseWindowStream()
  }
}

const getShared = () => shared
const getIdle = () => IDLE

/** One filtered stream (a run or a record) with its own state; replays from the start so a trace is complete. */
function useFilteredStream(runId: string | undefined, ref: string | undefined, enabled: boolean): AutoStreamState {
  const subscribe = useMemo(() => {
    let local: AutoStreamState = IDLE
    const listeners = new Set<() => void>()
    const emit = (next: AutoStreamState) => {
      local = next
      listeners.forEach((l) => l())
    }
    let reader: ReaderHandle | null = null
    return {
      subscribe: (listener: () => void) => {
        listeners.add(listener)
        if (listeners.size === 1 && enabled) {
          emit(IDLE)
          reader = startReader(
            { runId, ref },
            (frame) => emit({ frames: [...local.frames, frame].slice(-MAX_FRAMES), connected: true, lastId: frame.id }),
            (connected) => {
              if (local.connected !== connected) emit({ ...local, connected })
            },
            null,
          )
        }
        return () => {
          listeners.delete(listener)
          if (listeners.size === 0 && reader) {
            reader.stop()
            reader = null
          }
        }
      },
      get: () => local,
    }
  }, [runId, ref, enabled])
  return useSyncExternalStore(subscribe.subscribe, subscribe.get, getIdle)
}

/** Frames for the window, one run, one record or some kinds. */
export function useAutoStream(opts?: AutoStreamOptions): AutoStreamState {
  const runId = opts?.runId
  const ref = opts?.ref
  const kinds = opts?.kinds
  const filtered = Boolean(runId || ref)
  const kindsKey = (kinds ?? []).join(',')

  const windowState = useSyncExternalStore(filtered ? () => () => undefined : subscribeShared, getShared, getIdle)
  const filteredState = useFilteredStream(runId, ref, filtered)
  const state = filtered ? filteredState : windowState

  return useMemo<AutoStreamState>(() => {
    if (!kinds || kinds.length === 0) return state
    return { ...state, frames: state.frames.filter((f) => matchesKinds(f, kinds)) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, kindsKey])
}
